import { Suspense, lazy, useContext } from 'react';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import { AllieLogoLoading, AllieMainLayout } from '@allie/app-ui-lib';
import { DEFAULT_PAGE, DEPRECATED_PAGES, useAllieStorage, useSessionRecording2 } from './hooks';
import { FactoryGptWidget } from './components/FactoryGptWidget/FactoryGptWidget';
import { CONFIG_LOADING, ROUTE_CONFIG_LOADING } from './helpers/configLoading';
import { LoginPage, ResetPasswordPage, ValidateTokenPage } from './pages';
import { Page, LoginModal, GetTabs } from './components';
import { TrackingEvents } from './helpers/tracking-events';
import { isMobile, isSmartTV } from './helpers/utils';
import { AppContext } from './context/AppProvider';
import { AppMainProvider } from './context';
import { config } from './helpers';
import '@allie/app-ui-lib/dist/static/index.css';
import './App.scss';

setupIonicReact();

const MenuStationsRotation = lazy(() => import('./components/Menu/Menu.stationsRotation'));
const MenuDesktopV1 = lazy(() => import('./components/Menu/Menu.desktopV1'));
const MenuMobile = lazy(() => import('./components/Menu/Menu.mobile'));

const Menu = isMobile && !isSmartTV ? MenuMobile : MenuDesktopV1;

const App: React.FC = () => {
  const { setLang } = useContext(AppContext);
  const { getPermissions, getLoggedUser } = useAllieStorage();
  const { factory_gpt } = getPermissions();
  config.TEST_ENV && useSessionRecording2();

  const DerivedMenu = isSmartTV ? MenuStationsRotation : Menu;
  setLang?.((getLoggedUser().preferedLanguage || 'ES').toUpperCase());

  return (
    <AppMainProvider>
      <IonApp>
        <BrowserRouter>
          <IonRouterOutlet className="main-router">
            <Suspense fallback={<></>}>
              <Switch>
                {DEPRECATED_PAGES.map((page) => (
                  <Route path={page} exact={false}>
                    <Redirect to={DEFAULT_PAGE} />
                  </Route>
                ))}
                <Route path="/login" exact={false}>
                  <LoginPage basePath="login" />
                </Route>
                <Route path={'/app/action/reset-password'} exact={false}>
                  <ResetPasswordPage />
                </Route>
                <Route path="/app/action/assign-stop-reason/:stopId">
                  <ValidateTokenPage
                    tokenContext="dl_stop_assignment"
                    redirectUrl="/efficiency/assign-stop-reason/:stopId"
                  />
                </Route>
                <Route path="/app/action/factory-gpt/reports/:reportType">
                  <ValidateTokenPage
                    redirectUrl="/factory-gpt/reports/:reportType"
                    tokenContext="factory_gpt"
                    trackingEvent={
                      TrackingEvents.TOKEN_REDIRECT.factorygpt_report_link_clicked_email
                    }
                  />
                </Route>
                <Route path="/app/action/factory-gpt/alerts/:threadId">
                  <ValidateTokenPage
                    redirectUrl="/factory-gpt/alerts/:threadId"
                    tokenContext="factory_gpt"
                  />
                </Route>
                <Route path="/:name?" exact={false}>
                  <AllieMainLayout
                    menu={DerivedMenu}
                    page={Page}
                    tabs={GetTabs()}
                    tabSelected={window.location.pathname}
                  />
                </Route>
              </Switch>
            </Suspense>
          </IonRouterOutlet>
        </BrowserRouter>
        {factory_gpt?.view && <FactoryGptWidget />}
        <div id="main-drawer-portal"></div>
        <div id="main-modal-portal"></div>
        <AllieLogoLoading config={CONFIG_LOADING} routeConfig={ROUTE_CONFIG_LOADING} />
        <LoginModal />
      </IonApp>
    </AppMainProvider>
  );
};

export default App;
